/* global globalThis */
import { getValidToken } from "../../utils/getValidToken";

const BASE_URL = process.env.REACT_APP_BACKEND_API_URL?process.env.REACT_APP_BACKEND_API_URL+'/api':"http://localhost:8000/api";

function addParam(params, key, value, excludeAll = false) {
  if (value && (!excludeAll || value !== "All")) {
    params[key] = value;
  }
}

// Shared fetch helper: attaches the current access token and transparently
// force-refreshes + retries once when the backend rejects it with 401.
// This keeps LTCP calls working even after the access token has expired
// (e.g. the app has been left open for a long time).
// Set `throwOnError: false` when the caller inspects resp.ok/status directly
// (e.g. mutation calls that intentionally return a boolean on failure).
async function ltcpFetch(url, options = {}) {
  const { throwOnError = true, ...fetchOptions } = options;
  const attempt = async (forceRefresh = false) => {
    const authToken = await getValidToken({ forceRefresh });
    return fetch(url, {
      ...fetchOptions,
      headers: {
        ...fetchOptions.headers,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });
  };

  let resp = await attempt(false);

  if (resp.status === 401) {
    resp = await attempt(true);
  }

  if (throwOnError && !resp.ok) {
    throw new Error(`HTTP error! status: ${resp.status}`);
  }

  return resp;
}

// This api client will collect the filter values based on the user role
// and access and the currently applied filters.
export async function getCuFilters(email, filters) {
  const params = { user_email: email };

  if (filters) {
    const {
      demandFile,
      bgState,
      subcategoryState,
      branchState,
      brandState,
      formatState,
      basepackState,
      factoryState,
      runtime,
      runtype,
    } = filters;

    addParam(params, "runtime", runtime);
    addParam(params, "runtype", runtype);
    addParam(params, "demand_file", demandFile, true);
    addParam(params, "bg", bgState, true);
    addParam(params, "subcategory", subcategoryState, true);
    addParam(params, "branch", branchState, true);
    addParam(params, "brand", brandState, true);
    addParam(params, "format", formatState, true);
    addParam(params, "basepack", basepackState, true);
    addParam(params, "factory", factoryState, true);
  }

  const resp = await ltcpFetch(
    BASE_URL + "/cu_filters?" + new URLSearchParams(params), {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the filter values based on the user role
// and access and the currently applied filters.
export async function getDemandFilters(email, filters) {
  const params = { user_email: email };

  if (filters) {
    const {
      demandFile,
      bgState,
      subcategoryState,
      branchState,
      clusterState,
      depotState,
      brandState,
      format1State,
      formatState,
      basepackState,
      runtime,
      runtype,
      time_gran,
      year_3,
    } = filters;

    addParam(params, "year_3", year_3);
    addParam(params, "runtime", runtime);
    addParam(params, "runtype", runtype);
    addParam(params, "time_gran", time_gran);
    addParam(params, "demand_file", demandFile, true);
    addParam(params, "bg", bgState, true);
    addParam(params, "subcategory", subcategoryState, true);
    addParam(params, "branch", branchState, true);
    addParam(params, "cluster", clusterState, true);
    addParam(params, "depot", depotState, true);
    addParam(params, "brand", brandState, true);
    addParam(params, "format1", format1State, true);
    addParam(params, "format", formatState, true);
    addParam(params, "basepack", basepackState, true);
  }
  const resp = await ltcpFetch(
    BASE_URL + "/demand_filters?" + new URLSearchParams(params), {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the filter values based on the user role
// and access and the currently applied filters.
export async function getCapacityFilters(email, filters) {
  const params = { user_email: email };

  if (filters) {
    const {
      productTypeState,
      factoryState,
      basepackState,
      cascadeState,
      lineState,
      yearState,
      runtime,
      runtype,
    } = filters;

    addParam(params, "runtime", runtime);
    addParam(params, "runtype", runtype);
    addParam(params, "product_type", productTypeState, true);
    addParam(params, "factory", factoryState, true);
    addParam(params, "basepack", basepackState, true);
    addParam(params, "cascade", cascadeState, true);
    addParam(params, "line", lineState, true);
    addParam(params, "year", yearState, true);
  }

  const resp = await ltcpFetch(
    BASE_URL + "/capacity_filters?" + new URLSearchParams(params), {}
  );

  const data = await resp.json();

  return data;
}

// This api client collect a static data from the backend without any filter
export async function getAlertBranchFactoryMap() {
  const resp = await ltcpFetch(BASE_URL + "/alert_branch_factory_map", {});
  const data = await resp.json();

  return data;
}

// This api client will collect the filter values based on the user role
// and access and the currently applied filters.
export async function getAlertFilters(email, filters) {
  const params = { user_email: email };

  if (filters) {
    const {
      demand_file,
      bg,
      subcategory,
      location,
      format,
      table_level,
      runtime,
      runtype,
      productType,
    } = filters;

    addParam(params, "runtime", runtime);
    addParam(params, "runtype", runtype);
    addParam(params, "demand_file", demand_file, true);
    addParam(params, "bg", bg, true);
    addParam(params, "subcategory", subcategory, true);
    addParam(params, "location", location, true);
    addParam(params, "format", format, true);
    addParam(params, "table_level", table_level, true);
    addParam(params, "producttype", productType, true);
  }

  const resp = await ltcpFetch(
    BASE_URL + "/alert_filters?" + new URLSearchParams(params), {}
  );

  const data = await resp.json();

  return data;
}

export async function getCuAlertBasepackData(userEmail, filters) {
  const response = await ltcpFetch( BASE_URL +"/cu_alert_data?"+ 
    new URLSearchParams({
      user_email: userEmail,
      demand_file: filters.demandFile,
      bg: filters.bgState,
      subcategory: filters.subcategoryState,
      format: filters.format,
      location: filters.locationState,
      table_level: filters.tableLevel,
      producttype: filters.productType,
      runtime: filters.runtime,
      runtype: filters.runtype,
      threshold: filters.threshold,
    }
  ), {});
  const data = await response.json();
  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getDashboardCuData(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    tableGranularity,
    runtime,
    runtype,
    month_18,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/dashboard_cu_data?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        table_gran: tableGranularity,
        runtime,
        runtype,
        month_18,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getDashboardCuNestedData(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    tableGranularity,
    runtime,
    runtype,
    month_18,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/dashboard_cu_nested_data?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        table_gran: tableGranularity,
        runtime,
        runtype,
        month_18,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getDashboardDemanMap(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    mapGranularity,
    runtime,
    runtype,
    prod_type,
    month_18,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/dashboard_demand_map?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        map_gran: mapGranularity,
        runtime,
        runtype,
        prod_type,
        month_18,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getCapacityData(
  email,
  {
    demandFile,
    productTypeState,
    factoryState,
    basepackState,
    cascadeState,
    lineState,
    yearState,
    monthState,
    quarterState,
    timeGranularity,
    runtime,
    runtype,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/capacity_data?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        product_type: productTypeState,
        factory: factoryState,
        basepack: basepackState,
        cascade: cascadeState,
        line: lineState,
        year: yearState,
        month: monthState,
        quarter: quarterState,
        time_gran: timeGranularity,
        runtime,
        runtype,
      }),
    {}
  );
  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getCuData(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    runtime,
    runtype,
    month_18,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/cu_data?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        runtime,
        runtype,
        month_18,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getCuDataL1(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    variant,
    runtime,
    runtype,
    month_18,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/cu_data_l1?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        variant,
        runtime,
        runtype,
        month_18,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getCuDataL2(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    variant,
    runtime,
    runtype,
    month_18,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/cu_data_l2?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        variant,
        runtime,
        runtype,
        month_18,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getAlertData(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    locationState,
    formatState,
    tableLevel,
    productType,
    runtime,
    runtype,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/alert_data?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        location: locationState,
        format: formatState,
        table_level: tableLevel,
        producttype: productType,
        runtime,
        runtype,
      }),
    {}
  );
  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getAlertNestedData(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    alert,
    format,
    tableLevel,
    runtime,
    runtype,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/alert_nested_data?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        alert,
        format,
        table_level: tableLevel,
        runtime,
        runtype,
      }),
    {}
  );
  const text = await resp.text();
  const data = JSON.parse(text);

  return data;
}

// This api client will collect the values based on the user role and access.
export async function getUploadFilters(email) {
  const resp = await ltcpFetch(
    BASE_URL + "/upload_filter?" + new URLSearchParams({ user_email: email }),
    {}
  );
  const data = await resp.json();

  return data;
}

export async function getPipelineRunFilters(email) {
  const resp = await ltcpFetch(
    BASE_URL + "/pipeline_filter?" + new URLSearchParams({ user_email: email }),
    {}
  );
  const data = await resp.json();
  return data;
}

// This api client collect a static data from the backend without any filter
export async function getUploadInfo() {
  const resp = await ltcpFetch(BASE_URL + "/upload_info", {});
  const data = await resp.json();

  return data;
}

// This api client will help the user to download the specific file with the
// given product type.
export async function downloadFile(filename, product_type) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/download_file?" +
      new URLSearchParams({ mastername: filename, product_type }),
    {},
  );
  const blob = await resp.blob();
  const url = globalThis.URL.createObjectURL(blob);
  const contentDisposition = resp.headers?.get("content-disposition");
  const responseFilename = contentDisposition?.match(
    /filename="?([^";]+)"?/i,
  )?.[1];
  const link = document.createElement("a");

  link.href = url;
  link.download =
    responseFilename ||
    (filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}


// This api client will help the user to upload the specific excel file as
// multipart form data for the given product type.
export async function uploadFile(filename, file, product_type, username) {
  const formData = new FormData();

  formData.append("file", file);
  formData.append("filename", filename);
  formData.append("username", username);
  formData.append("product_type", product_type);

  const resp = await ltcpFetch(BASE_URL + "/upload_file", {
    method: "POST",
    body: formData,
    throwOnError: false,
  });

  return resp;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getDemandData(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    clusterState,
    depotState,
    brandState,
    format1State,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    runtime,
    runtype,
    year_3,
    table_level
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/demand_data?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        cluster: clusterState,
        depot: depotState,
        brand: brandState,
        format1: format1State,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        runtime,
        runtype,
        year_3,
        table_level: table_level,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getDemandL1Data(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    clusterState,
    depotState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    runtime,
    runtype,
    year_3,
    tableLevel,      // NEW
    branch_code,     // NEW
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/demand_data_l1?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        cluster: clusterState,
        depot: depotState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        runtime,
        runtype,
        year_3,
        table_level: tableLevel || 'national',
        branch_code: branch_code || '',
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getDemandL2Data(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    clusterState,
    depotState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    format_l1,
    runtime,
    runtype,
    year_3,
    tableLevel,      // NEW
    branch_code,     // NEW
    brand_filter,    // NEW
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/demand_data_l2?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        cluster: clusterState,
        depot: depotState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        format_l1: format_l1 || '',
        runtime,
        runtype,
        year_3,
        table_level: tableLevel || 'national',
        branch_code: branch_code || '',
        brand_filter: brand_filter || '',
      }),
    {}
  );

  const data = await resp.json();

  return data;
}
// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getDemandL3Data(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    clusterState,
    depotState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    format_l1,
    format_l2,
    runtime,
    runtype,
    year_3,
    tableLevel,      // NEW
    branch_code,     // NEW
    brand_filter,    // NEW
  }
) {
  const resp = await fetch(
    BASE_URL +
      "/demand_data_l3?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        cluster: clusterState,
        depot: depotState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        format_l1: format_l1 || '',
        format_l2: format_l2 || '',
        runtime,
        runtype,
        year_3,
        table_level: tableLevel || 'national',
        branch_code: branch_code || '',
        brand_filter: brand_filter || '',
      })
  );

  const data = await resp.json();

  return data;
}


// NEW - Add getDemandL4Data (only for branch level)
export async function getDemandL4Data(
  email,
  {
    demandFile,
    bgState,
    subcategoryState,
    branchState,
    clusterState,
    depotState,
    brandState,
    formatState,
    basepackState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    runtime,
    runtype,
    year_3,
    branch_code,
    brand_filter,
    format_l1,
    format_l2,
  }
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/demand_data_l4?" +
      new URLSearchParams({
        user_email: email,
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchState,
        cluster: clusterState,
        depot: depotState,
        brand: brandState,
        format: formatState,
        basepack: basepackState,
        factory: factoryState,
        year: yearState,
        quarter: quarterState,
        month: monthState,
        time_gran: timeGranularity,
        runtime,
        runtype,
        year_3,
        branch_code,
        brand_filter,
        format_l1,
        format_l2,
      }),
    {}
  );

  const data = await resp.json();
  return data;
}


// NEW - sinlge apiclient for demand data with hierarchy level selection
export async function getDemandChildData(userEmail, payload) {
  const params = {
    user_email: userEmail,
    demand_file: payload.demandFile,

    table_level: payload.tableLevel,
    level: payload.level,

    bg: payload.bgState,
    subcategory: payload.subcategoryState,
    branch: payload.branchState,
    cluster: payload.clusterState,
    depot: payload.depotState,
    brand: payload.brandState,
    format1: payload.format1State,
    format: payload.formatState,
    basepack: payload.basepackState,

    year: payload.yearState,
    quarter: payload.quarterState,
    month: payload.monthState,
    time_gran: payload.timeGranularity,

    runtime: payload.runtime,
    runtype: payload.runtype,
    year_3: payload.year_3,

    parent_branch: payload.parentBranch,
    parent_subcategory: payload.parentSubcategory,
    parent_brand: payload.parentBrand,
    parent_format_l1: payload.parentFormatL1,
    parent_format_l2: payload.parentFormatL2,
  };

  const cleanedParams = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== ""
    )
  );

  const resp = await ltcpFetch(
    BASE_URL +"/demand_data_children?" +
    new URLSearchParams(cleanedParams),
    {}
  );

  const data = await resp.json();
  return data;
}


// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getRecommendationPrebuild({
  factory,
  format,
  demandFile,
  runtime,
  runtype,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/recommendation_prebuilt?" +
      new URLSearchParams({
        demand_file: demandFile,
        alert_type: factory,
        format,
        runtime,
        runtype,
      }),
    {}
  );
  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getRecommendationCrossSourcing({
  factory,
  format,
  demandFile,
  runtime,
  runtype,
  user_email,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/recommendation_cross_sourcing?" +
      new URLSearchParams({
        factory,
        format,
        demand_file: demandFile,
        runtime,
        runtype,
        user_email,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function getRecommendationCrossSourcingNested({
  factory,
  from_factory,
  format,
  demandFile,
  runtime,
  runtype,
  user_email,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/recommendation_cross_sourcing_nested?" +
      new URLSearchParams({
        factory,
        from_factory,
        format,
        demand_file: demandFile,
        runtime,
        runtype,
        user_email,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

// This api client will collect the values based on the user role and access
// and the currently applied filters.
export async function triggerSqlToAdfPipeline({
  producttype,
  manualone,
  manualtwo,
  user_email,
  isDataCheck
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/trigger_cu?" +
      new URLSearchParams({ producttype, manualone, manualtwo, user_email, isDataCheck }),
    { throwOnError: false }
  );
  if (resp.status === 200) {
    const data = await resp.json();
    return Boolean(data.run_id);
  }
  return false;
}

// This api client collect a static data from the backend without any filter
export async function getRoleManagementData() {
  const resp = await ltcpFetch(BASE_URL + "/get_all_roles", {});
  const data = await resp.json();

  return data;
}

// This api client will help the user to send the user details with role and
// access to add in the AD group and in the access list.
export async function addNewRole({ email, write, bg, category, level }) {
  const payload = {
    email,
    write,
    all: level === "hul",
    mdm: level === "mdm",
    role: level === "role",
    bg: level === "bg" ? bg : null,
    category: level === "category" ? category : null,
  };

  const resp = await ltcpFetch(BASE_URL + "/add_new_role", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    throwOnError: false,
  });

  return resp.status === 200;
}

// This api client will help the user to update the user access and role based
// on the newly given access and role.
export async function updateUserRole({ email, write, bg, category, level }) {
  const payload = {
    email,
    write,
    all: level === "hul",
    mdm: level === "mdm",
    role: level === "role",
    bg: level === "bg" ? bg : null,
    category: level === "category" ? category : null,
  };

  const resp = await ltcpFetch(BASE_URL + "/update_role", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    throwOnError: false,
  });

  return resp.status === 200;
}

// This api client will help the user to delete the user from the AD group and
// the access list in sql table.
export async function deleteUserRole(email) {
  const resp = await ltcpFetch(BASE_URL + "/delete_role", {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
    throwOnError: false,
  });

  return resp.status === 200;
}

// This api client will help the frontend to get the currently logged in user
// role and access details
export async function getUserRole(email) {
  const resp = await ltcpFetch(
    BASE_URL + "/get_user_role?" + new URLSearchParams({ user_email: email }),
    {}
  );

  const data = await resp.json();
  return data;
}

// This api client will help the frontend to collect the run details to show on
// landing page filters based on the user role and access.
export async function getRunDetails(email) {
  const resp = await ltcpFetch(
    BASE_URL + "/get_run_details?" + new URLSearchParams({ user_email: email }), {}
  );

  const data = await resp.json();

  return data;
}

const authReportFetch = async (path, filename) => {
  const resp = await ltcpFetch(BASE_URL + path, {});
  const blob = await resp.blob();
  const url = globalThis.URL.createObjectURL(blob);
  globalThis.open(url, "_blank");
};

export async function historicalDemandReport({
  producttype,
  runtime,
  runtype,
  user_email,
  demandtype,
}) {
  await authReportFetch(
    "/report_historical_demand?" +
      new URLSearchParams({
        producttype,
        runtype,
        runtime,
        user_email,
        demandtype,
      }),
    "historical_demand_report.xlsx"
  );
}

export async function capacityAllocationReport({
  producttype,
  runtime,
  runtype,
  time_gran,
  user_email,
  demand_type,
}) {
  await authReportFetch(
    "/report_capacity_allocation?" +
      new URLSearchParams({
        producttype,
        runtime,
        runtype,
        time_gran,
        user_email,
        demand_type,
      }),
    "capacity_allocation_report.xlsx"
  );
}

export async function demandDisaggregation({
  producttype,
  runtime,
  runtype,
  demandtype,
  user_email,
}) {
  await authReportFetch(
    "/report_demand_disaggregation?" +
      new URLSearchParams({
        producttype,
        runtime,
        runtype,
        demandtype,
        user_email,
      }),
    "demand_disaggregation.xlsx"
  );
}

export async function alertReport({
  producttype,
  runtype,
  runtime,
  user_email,
  demand_type,
}) {
  await authReportFetch(
    "/report_alert?" +
      new URLSearchParams({
        producttype,
        runtype,
        runtime,
        user_email,
        demand_type,
      }),
    "alert_report.xlsx"
  );
}

export async function demandLossReport({
  producttype,
  runtype,
  runtime,
  demandtype,
  report,
  user_email,
}) {
  await authReportFetch(
    "/report_demand_loss?" +
      new URLSearchParams({
        producttype,
        runtype,
        runtime,
        demandtype,
        report,
        user_email,
      }),
    `demand_loss_report_${report}.xlsx`
  );
}

// This api client collect a static data from the backend without any filter
export async function systemSourcingMatrixReport() {
  await authReportFetch(
    "/report_system_sourcing_matrix",
    "system_sourcing_matrix.xlsx"
  );
}

export async function getAlertCount({
  user_email,
  demand_file,
  bg,
  subcategory,
  branch,
  format,
  factory,
  runtime,
  runtype,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/alerts_count?" +
      new URLSearchParams({
        user_email,
        demand_file,
        bg,
        subcategory,
        branch,
        format,
        factory,
        runtime,
        runtype,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function getRecommendationLineSpeed({
  demand_file,
  factory,
  runtime,
  runtype,
  format,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/recommendation_line_speed?" +
      new URLSearchParams({
        demand_file,
        factory,
        runtime,
        runtype,
        format,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function getRecommendationLineSpeedOR({
  demand_file,
  factory,
  runtime,
  runtype,
  format,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/recommendation_line_speed_or?" +
      new URLSearchParams({ demand_file, factory, runtime, runtype, format }),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function getRecommendationComparison({
  demand_file,
  factory,
  runtime,
  runtype,
  format,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/recommendation_comparison?" +
      new URLSearchParams({ demand_file, factory, runtime, runtype, format }),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function getSimulationFilters(user_email, filters) {
  const params = { user_email };

  if (filters) {
    const { demand_file, product_type, factory, runtime, runtype } = filters;

    addParam(params, "demand_file", demand_file);
    addParam(params, "product_type", product_type);
    addParam(params, "factory", factory);
    addParam(params, "runtime", runtime);
    addParam(params, "runtype", runtype);
  }

  const resp = await ltcpFetch(
    BASE_URL + "/simulation_filters?" + new URLSearchParams(params),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function getSimulationLineSpeedOR({
  demand_file,
  product_type,
  factory,
  runtime,
  runtype,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/simulation_line_speed_or?" +
      new URLSearchParams({
        demand_file,
        product_type,
        factory,
        runtime,
        runtype,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function getSimulationLineSpeed({
  demand_file,
  product_type,
  factory,
  runtime,
  runtype,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/simulation_line_speed?" +
      new URLSearchParams({
        demand_file,
        product_type,
        factory,
        runtime,
        runtype,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function getDemandSummaryReport({
  runtype,
  runtime,
  demand_type,
  time_gran,
  user_email,
  product_type,
}) {
  await authReportFetch(
    "/report_demand_summary?" +
      new URLSearchParams({
        runtype,
        runtime,
        demand_type,
        time_gran,
        user_email,
        product_type,
      }),
    "demand_summary_report.xlsx"
  );
}
export async function getDataCheckFailedFiles({
  type,
  user_email,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/data_check_file?" +
      new URLSearchParams({
        type,
        user_email,
      }),
    {}
  );

const blob = await resp.blob();
const url = globalThis.URL.createObjectURL(blob);

const a = document.createElement("a");
a.href = url;
a.download = `${type}.xlsx`;

document.body.appendChild(a);
a.click();
a.remove();

globalThis.URL.revokeObjectURL(url);
}

// This api client collect a static data from the backend without any filter
export async function getAlertThreshold() {
  const resp = await ltcpFetch(BASE_URL + "/get_cu_threshold", {});

  const data = await resp.json();

  return data;
}

export async function getCapacityDefault({ demand_file, runtype, runtime }) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/capacity_default?" +
      new URLSearchParams({
        demand_file,
        runtype,
        runtime,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function acceptCS({
  runtime,
  runtype,
  user_email,
  demandtype,
  format,
  factory,
  period,
  demand,
  capacity,
  cu,
  months,
  potential_cs,
  cu_after_cs,
  brand_variants,
}) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/accept_cs?" +
      new URLSearchParams({
        runtime,
        runtype,
        user_email,
        demandtype,
        format,
        factory,
        period,
        demand,
        capacity,
        cu,
        months,
        potential_cs,
        cu_after_cs,
        brand_variants,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}

export async function resetCS({
  runtime,
  runtype,
  user_email,
  demandtype,
  format,
}) {
  await ltcpFetch(
    BASE_URL +
      "/reset_cs?" +
      new URLSearchParams({
        runtime,
        runtype,
        user_email,
        demandtype,
        format,
      }),
    {}
  );
}

export async function getProductTypeOfFormatL2(format) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/formatl2_to_producttype?" +
      new URLSearchParams({
        format,
      }),
    {}
  );

  const data = await resp.json();

  return data;
}
export async function getClusterData(user_email) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/branchcluster?" +
      new URLSearchParams({
        user_email,
      }),
    {}
  );
  const data = await resp.json();
  return data;
}
export async function getPipelineRunStatus(
  runtime,
  runtype,
  user_email,
  producttype
) {
  const resp = await ltcpFetch(
    BASE_URL +
      "/run_status?" +
      new URLSearchParams({
        runtime,
        runtype,
        user_email,
        producttype,
      }),
    {}
  );

  const data = await resp.json();
  return data;
}
